import { useState, useEffect } from 'react'
import { getTosFromStorage, setTosToStorage } from '../lib/tosStateHelpers'

type UseTosStateParams = {
  setTosAccepted: (value: boolean) => void
  tosStorageKey: string
  prepaidTosStorageKey: string
  hasFunds: boolean
  hasUnifiedAgreementCheckboxes: boolean
}

type UseTosStateReturn = {
  acceptedTermsOfService: boolean
  acceptedPrepaidTermsOfService: boolean
  onTosCheckboxChange: () => void
  onPrepaidTosChange: () => void
}

/**
 * Manages TOS acceptance state with localStorage persistence.
 * Handles unified checkbox logic for prepaid scenarios and
 * propagates acceptance status upward via setTosAccepted.
 */
export const useTosState = ({
  setTosAccepted,
  tosStorageKey,
  prepaidTosStorageKey,
  hasFunds,
  hasUnifiedAgreementCheckboxes
}: UseTosStateParams): UseTosStateReturn => {
  const [acceptedTermsOfService, setAcceptedTermsOfService] = useState(() =>
    getTosFromStorage(tosStorageKey)
  )
  const [
    acceptedPrepaidTermsOfService,
    setAcceptedPrepaidTermsOfService
  ] = useState(() => getTosFromStorage(prepaidTosStorageKey))

  const onTosCheckboxChange = () => {
    const newAcceptedTermsValue = !acceptedTermsOfService
    if (hasUnifiedAgreementCheckboxes && hasFunds) {
      const newAcceptedPrepaidValue = !acceptedPrepaidTermsOfService
      setAcceptedPrepaidTermsOfService(newAcceptedPrepaidValue)
      setTosToStorage(prepaidTosStorageKey, newAcceptedPrepaidValue)
    }
    setAcceptedTermsOfService(newAcceptedTermsValue)
    setTosToStorage(tosStorageKey, newAcceptedTermsValue)
  }

  const onPrepaidTosChange = () => {
    const newValue = !acceptedPrepaidTermsOfService
    setAcceptedPrepaidTermsOfService(newValue)
    setTosToStorage(prepaidTosStorageKey, newValue)
  }

  useEffect(() => {
    const isUnifiedScenario = hasUnifiedAgreementCheckboxes && hasFunds
    const prepaidAccepted = hasFunds ? acceptedPrepaidTermsOfService : true
    const canContinue = isUnifiedScenario
      ? acceptedTermsOfService
      : acceptedTermsOfService && prepaidAccepted
    setTosAccepted(canContinue)
  }, [
    acceptedTermsOfService,
    acceptedPrepaidTermsOfService,
    setTosAccepted,
    hasFunds,
    hasUnifiedAgreementCheckboxes
  ])

  return {
    acceptedTermsOfService,
    acceptedPrepaidTermsOfService,
    onTosCheckboxChange,
    onPrepaidTosChange
  }
}
